import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import { Check, Eye, Pencil, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";

import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { SplitActionButton } from "@/components/SplitActionButton";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AdvanceDetailDialog } from "@/components/requests/AdvanceDetailDialog";
import { DownloadReportButton } from "@/components/requests/DownloadReportButton";
import { RequestDetailDialog } from "@/components/requests/RequestDetailDialog";
import { TripCell } from "@/components/requests/TripCell";
import { AdvanceAmountsCell } from "@/components/requests/AdvanceAmountsCell";
import {
  ReviewAdvanceDialog,
  ReviewAdvanceFields
} from "@/components/requests/ReviewAdvanceDialog";
import {
  ReviewRequestDialog,
  ReviewRequestFields
} from "@/components/requests/ReviewRequestDialog";
import {
  AmountCellById,
  SettlementAmountCellById
} from "@/components/requests/SettlementAmountCell";
import { DataTable } from "@/components/data-table/DataTable";
import { SortableHeader } from "@/components/data-table/SortableHeader";
import { type AppTableFeatures } from "@/components/data-table/features";
import SegmentedTabs from "@/components/SegmentedTabs";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { RefreshButton } from "@/components/RefreshButton";
import { FinanceNoticeDialog } from "@/components/requests/FinanceNoticeDialog";
import {
  ApiError,
  approveAdvanceAndNotifyFinance,
  approveAndNotifyFinance,
  decideAdvanceApproval,
  decideApproval,
  fetchMyAdvanceApprovals,
  fetchMyApprovals,
  resubmitAdvanceApproval,
  resubmitApproval,
  reviewAdvanceApproval,
  reviewApproval
} from "../api";
import { AdvanceApproval, Approval, ApprovalStatus, requestTypeLabel } from "../types";
import {
  APPROVAL_DISPLAY_STATUS_LABEL,
  approvalDisplayStatus,
  isActuallyPending
} from "@/lib/approval";
import { tripRoute } from "@/lib/trip";
import { formatDate } from "@/lib/datetime";

type View = "requests" | "advances";
// "ResubmitRequested"/"Resubmit" are each entity's OWN display status (see
// lib/approval.ts's approvalDisplayStatus), not a value approval.status
// itself ever holds - same split ApproverQueue.tsx's StatusFilter/
// DisplayStatus already makes for the Finance side. A Manager has nothing
// to do on one of these until the employee resubmits, so (unlike the other
// three values) it's left out of "All"/"Pending" - see the data/advanceData
// filters below - and only shown when this tab is picked on purpose.
type StatusFilter = "All" | ApprovalStatus | "ResubmitRequested";
type AdvanceStatusFilter = "All" | ApprovalStatus | "Resubmit";

const EMPTY_COPY: Record<StatusFilter, string> = {
  All: "Nothing has been routed to you for approval.",
  Pending: "No pending approvals.",
  ResubmitRequested: "No requests are awaiting changes from the employee.",
  Approved: "No approved decisions yet.",
  Rejected: "No rejected decisions yet."
};

const ADVANCE_EMPTY_COPY: Record<AdvanceStatusFilter, string> = {
  All: "No advance requests have been routed to you.",
  Pending: "No pending advance requests.",
  Resubmit: "No advances are awaiting changes from the employee.",
  Approved: "No approved advances yet.",
  Rejected: "No rejected advances yet."
};

const columnHelper = createColumnHelper<AppTableFeatures, Approval>();
const advanceColumnHelper = createColumnHelper<
  AppTableFeatures,
  AdvanceApproval
>();

/** Small red count pill next to a tab label - "there's something here
 *  needing your action," same idea as an unread-message badge (see
 *  ApproverQueue.tsx, which uses the identical pattern). Caller only
 *  renders it when count > 0, so it never shows a bare "0". Capped
 *  display at "9+" so a large count doesn't blow out the tab's width. */
function TabCountBadge({ count }: { count: number }) {
  return (
    <span className="flex h-[1.125rem] min-w-[1.125rem] items-center justify-center rounded-full bg-destructive px-1 text-[0.625rem] font-semibold leading-none text-destructive-foreground">
      {count > 9 ? "9+" : count}
    </span>
  );
}

export default function MyApprovals() {
  // Hoisted above every other piece of state in this component specifically
  // because `view`'s own initializer (right below) needs it - a notification
  // deep-link (?request=<id> / ?advance=<id>, see notifications.py's
  // _dispatch) must land on the MATCHING tab, not always "requests," or
  // closing the sheet it opened would show the wrong table entirely (the
  // bug this fixes: ?advance=<id> opened the right sheet, since
  // viewAdvanceId doesn't depend on `view`, but left the tab underneath on
  // "requests" regardless).
  const [searchParams] = useSearchParams();
  // ?view=advances (the Dashboard's "My Approvals" advances box, same
  // pattern as MyRequests.tsx/OtherReimbursements.tsx's own ?view= param)
  // also opens on the Advances tab, same as a notification's ?advance=<id>.
  const [view, setView] = React.useState<View>(() =>
    searchParams.get("view") === "advances" || searchParams.get("advance")
      ? "advances"
      : "requests"
  );

  const [rows, setRows] = React.useState<Approval[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [status, setStatus] = React.useState<StatusFilter>("Pending");
  const [decision, setDecision] = React.useState<{
    approval: Approval;
    verdict: ApprovalStatus;
  } | null>(null);
  const [comment, setComment] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [decisionError, setDecisionError] = React.useState<string | null>(null);

  const [advanceRows, setAdvanceRows] = React.useState<AdvanceApproval[]>([]);
  const [advancesLoading, setAdvancesLoading] = React.useState(true);
  const [advanceStatus, setAdvanceStatus] =
    React.useState<AdvanceStatusFilter>("Pending");
  const [advanceDecision, setAdvanceDecision] = React.useState<{
    approval: AdvanceApproval;
    verdict: ApprovalStatus;
  } | null>(null);
  const [advanceComment, setAdvanceComment] = React.useState("");
  const [advanceSaving, setAdvanceSaving] = React.useState(false);
  const [advanceDecisionError, setAdvanceDecisionError] = React.useState<
    string | null
  >(null);

  // Deep-link support: a notification's "View Request" button appends
  // ?request=<id> or ?advance=<id> (see notifications.py's _dispatch) so
  // clicking it opens straight to that one request's detail sheet instead
  // of landing on this page's plain list, with no way to tell which of
  // (potentially 50+) pending rows is the one the notification was about.
  // Read once on mount, same pattern MyRequests.tsx already uses for its
  // own view/status query params (searchParams itself is hoisted above -
  // see `view`'s own declaration for why).
  const [viewRequestId, setViewRequestId] = React.useState<string | null>(
    () => searchParams.get("request")
  );
  const [viewAdvanceId, setViewAdvanceId] = React.useState<string | null>(
    () => searchParams.get("advance")
  );
  // highlightOnOpen: the sheet's own one-shot flash, true ONLY for this
  // initial deep-linked open (a later manual "view" click from the table
  // reuses viewRequestId/viewAdvanceId without the flash, since there's
  // nothing to confirm arrival at in that case).
  const [highlightOnOpen, setHighlightOnOpen] = React.useState(
    () => !!searchParams.get("request") || !!searchParams.get("advance")
  );
  // Used for the sheet's own GENUINE close (X, outside click, Escape, or
  // its own footer action completing successfully and moving forward -
  // e.g. a saved Review proceeding to the Finance-notify popup) - clears
  // highlightOnOpen too, not just the id, so the next unrelated manual
  // "view" click doesn't inherit a stale flash flag.
  const closeViewRequestSheet = () => {
    setViewRequestId(null);
    setHighlightOnOpen(false);
  };
  const closeViewAdvanceSheet = () => {
    setViewAdvanceId(null);
    setHighlightOnOpen(false);
  };
  // "Stepping aside" - a footer action (Review/Approve/Reject/Resubmit)
  // needs the sheet closed so its own dialog doesn't double up two
  // semi-transparent overlays into solid black (see closeViewRequestSheet's
  // git history for that bug) - but closing outright was itself wrong: if
  // the employee then CANCELS that dialog without actually deciding
  // anything, they land on the bare table instead of back on the sheet
  // they were just reading, even though nothing they were looking at
  // actually changed. These remember "which sheet to bring back, and with
  // which id" so the review/decision dialog's own onOpenChange(false) -
  // which ONLY ever fires for a genuine user-driven cancel/X/outside-click/
  // Escape, never for that same dialog's own programmatic close-on-save -
  // can restore it. A plain table-row click never sets these, so canceling
  // a review opened that way correctly has nothing to restore.
  const [returnToRequestSheet, setReturnToRequestSheet] = React.useState<
    string | null
  >(null);
  const [returnToAdvanceSheet, setReturnToAdvanceSheet] = React.useState<
    string | null
  >(null);
  const stepAsideFromRequestSheet = () => {
    if (viewRequestId) setReturnToRequestSheet(viewRequestId);
    setViewRequestId(null);
  };
  const stepAsideFromAdvanceSheet = () => {
    if (viewAdvanceId) setReturnToAdvanceSheet(viewAdvanceId);
    setViewAdvanceId(null);
  };
  // Called from EVERY downstream dialog's own onOpenChange(false) below
  // (Review, the Approve/Reject ConfirmDialog, Resubmit) in place of that
  // dialog's plain "clear my own id" close - closes the dialog exactly as
  // before, AND, only if a sheet actually stepped aside to open it
  // (returnTo*Sheet is set), reopens that sheet. A review/decision opened
  // directly from a TABLE ROW never sets returnTo*Sheet, so this is a no-op
  // there - closes the dialog, nothing to restore, same as today. These are
  // NOT called from that same dialog's own SUCCESSFUL-save path (which
  // closes it programmatically, a separate code path - see
  // stepAsideFromRequestSheet's own doc comment) - a save moves the flow
  // forward (e.g. to the Finance-notify popup), where reopening a now-stale
  // sheet would be wrong, not a courtesy.
  const cancelAndMaybeReturnToRequestSheet = (closeDialog: () => void) => {
    closeDialog();
    if (returnToRequestSheet) {
      setViewRequestId(returnToRequestSheet);
      setReturnToRequestSheet(null);
    }
  };
  const cancelAndMaybeReturnToAdvanceSheet = (closeDialog: () => void) => {
    closeDialog();
    if (returnToAdvanceSheet) {
      setViewAdvanceId(returnToAdvanceSheet);
      setReturnToAdvanceSheet(null);
    }
  };
  // deepLinked{Request,Advance}Id: SEPARATE from viewRequestId/viewAdvanceId
  // specifically because those two get cleared to null the instant the
  // sheet closes (that's what "closed" means for a controlled dialog) - but
  // the whole point here is the table row needs to stay identifiable AFTER
  // closing, which a value that goes null on close can't drive. These
  // persist across close, and only clear on the two things the employee
  // actually asked for: clicking anywhere else on the page (see
  // clearDeepLinkHighlight, wired to the table's own onClick capture below)
  // or navigating away (which drops this component's state entirely, no
  // extra handling needed for that half).
  const [deepLinkedRequestId, setDeepLinkedRequestId] = React.useState(
    () => searchParams.get("request")
  );
  const [deepLinkedAdvanceId, setDeepLinkedAdvanceId] = React.useState(
    () => searchParams.get("advance")
  );
  const clearDeepLinkHighlight = () => {
    setDeepLinkedRequestId(null);
    setDeepLinkedAdvanceId(null);
  };

  // "Notify Finance" popup - shown right after clicking Approve (Travel
  // Requests AND Advances; Reject never opens this), in place of actually
  // deciding anything yet. Send-only: no cancel/backdrop/escape dismiss
  // (see FinanceNoticeDialog) - nothing is approved until Send is clicked
  // (approveAndNotifyFinance / approveAdvanceAndNotifyFinance, chosen by
  // `kind` below), so closing the tab here leaves the row exactly as it
  // was: still Pending, safe to approve again. `comment` carries the
  // Manager's note from the first confirm dialog through to that final
  // call, since the preview call no longer records anything.
  const [financeNotice, setFinanceNotice] = React.useState<{
    kind: "request" | "advance";
    id: string;
    toName: string | null;
    toEmail: string | null;
    comment?: string;
  } | null>(null);
  const [financeCc, setFinanceCc] = React.useState<string[]>([]);
  const [financeSending, setFinanceSending] = React.useState(false);
  const [financeError, setFinanceError] = React.useState<string | null>(null);

  // Review: opens an editable version of the detail dialog (own component,
  // not a mode of the read-only RequestDetailDialog/AdvanceDetailDialog) -
  // saving calls reviewApproval/reviewAdvanceApproval, which (like a plain
  // Approve) only PREVIEWS the Finance Approver; the actual commit reuses
  // the SAME approve-and-notify-finance call a plain Approve already uses,
  // via the existing financeNotice state above.
  const [reviewRequestId, setReviewRequestId] = React.useState<string | null>(
    null
  );
  const [reviewAdvanceId, setReviewAdvanceId] = React.useState<string | null>(
    null
  );
  const [reviewSaving, setReviewSaving] = React.useState(false);

  // Resubmit: single-click commit (no preview step) - a comment is
  // required so the employee knows what to fix.
  const [resubmitTarget, setResubmitTarget] = React.useState<{
    kind: "request" | "advance";
    id: string;
  } | null>(null);
  const [resubmitComment, setResubmitComment] = React.useState("");
  const [resubmitSaving, setResubmitSaving] = React.useState(false);
  const [resubmitError, setResubmitError] = React.useState<string | null>(
    null
  );

  // A stale decision (the row was already acted on elsewhere since this
  // table last loaded) now 409s instead of silently succeeding or
  // reusing the inline dialog error state, since it can surface from
  // several different actions (Approve/Reject/Review/Resubmit) - a toast
  // plus a reload is simpler than threading a dedicated error slot through
  // every one of them.
  const handleStaleDecision = (err: unknown, reload: () => void) => {
    if (err instanceof ApiError && err.status === 409) {
      toast.error(
        err.message ||
          "This request was already decided elsewhere (maybe another tab or approver) - the list has been refreshed to show its current status."
      );
      reload();
      return true;
    }
    return false;
  };

  const load = React.useCallback(() => {
    setLoading(true);
    fetchMyApprovals()
      .then(setRows)
      .finally(() => setLoading(false));
  }, []);
  React.useEffect(() => {
    load();
  }, [load]);

  const loadAdvances = React.useCallback(() => {
    setAdvancesLoading(true);
    fetchMyAdvanceApprovals()
      .then(setAdvanceRows)
      .finally(() => setAdvancesLoading(false));
  }, []);
  React.useEffect(() => {
    loadAdvances();
  }, [loadAdvances]);

  // Filters on the real per-row status (approvalDisplayStatus), not the raw
  // approval.status - same reasoning as isActuallyPending above. Without
  // this, "Pending" here would also include a row actually sitting in
  // ResubmitRequested/Resubmit (waiting on the EMPLOYEE, not this Manager),
  // same gap ApproverQueue.tsx's own displayStatus-based filter already
  // closed for Finance. Picking "ResubmitRequested"/"Resubmit" here on
  // purpose still works the same way - that value only ever matches a row
  // that's genuinely in that state.
  const data = React.useMemo(
    () =>
      status === "All"
        ? rows
        : rows.filter((r) => approvalDisplayStatus(r) === status),
    [rows, status]
  );

  const advanceData = React.useMemo(
    () =>
      advanceStatus === "All"
        ? advanceRows
        : advanceRows.filter(
            (r) => approvalDisplayStatus(r) === advanceStatus
          ),
    [advanceRows, advanceStatus]
  );

  // Tab badge counts - derived from the full unfiltered rows/advanceRows
  // (not `data`/`advanceData` above), so each stays accurate regardless of
  // which status tab is currently selected in that table.
  // Same isActuallyPending reasoning as the actions-column gate below - a
  // row actually sitting in ResubmitRequested/Resubmit doesn't need this
  // Manager's action right now, so it shouldn't inflate the tab badge
  // either (same bug this count had: it used to count every row still
  // reading raw status "Pending").
  const pendingRequestCount = React.useMemo(
    () => rows.filter(isActuallyPending).length,
    [rows]
  );
  const pendingAdvanceCount = React.useMemo(
    () => advanceRows.filter(isActuallyPending).length,
    [advanceRows]
  );

  const submitDecision = async () => {
    if (!decision) return;
    if (decision.verdict === "Rejected" && !comment.trim()) return;
    setSaving(true);
    setDecisionError(null);
    try {
      if (decision.verdict === "Rejected") {
        // Rejecting is immediate and final, exactly as before - no popup.
        await decideApproval(
          decision.approval.travelRequestId,
          "Rejected",
          comment.trim()
        );
        setDecision(null);
        setComment("");
        // Flow genuinely over (Reject has no further step, unlike Approve's
        // preview -> Finance-notice chain) - clear any pending return-to-
        // sheet now, or it would wrongly survive to reopen a stale sheet on
        // some LATER, unrelated dialog's cancel.
        setReturnToRequestSheet(null);
        load();
        return;
      }
      // Approved: this call only PREVIEWS who Finance would be - nothing
      // is decided yet (see decideApproval's own docstring). The request
      // stays exactly as it is until the "Notify Finance" popup's Send is
      // clicked (sendFinanceNotice below), which is the real approve.
      const preview = await decideApproval(
        decision.approval.travelRequestId,
        "Approved",
        comment || undefined
      );
      setFinanceCc([]);
      setFinanceError(null);
      setFinanceNotice({
        kind: "request",
        id: decision.approval.travelRequestId,
        toName: preview.financeApproverName,
        toEmail: preview.financeApproverEmail,
        comment: comment || undefined
      });
      setDecision(null);
      setComment("");
      // No load() here - nothing has actually changed yet, the row is
      // still Pending until sendFinanceNotice succeeds below.
    } catch (err) {
      // A stale decision (this row was already acted on in another tab/by
      // someone else since the table last loaded) closes the dialog with a
      // toast and reloads, instead of leaving a dialog open against a row
      // that's moved on - same handling as Review/Resubmit below. Any other
      // failure keeps the old behaviour: dialog stays open with an inline
      // error so the Manager can retry.
      if (!handleStaleDecision(err, load)) {
        setDecisionError(
          err instanceof ApiError
            ? err.message
            : `Could not ${decision.verdict === "Approved" ? "approve" : "reject"} this request - check your connection and try again.`
        );
      } else {
        setDecision(null);
        setComment("");
        // Same reasoning as the Reject-success branch above - this row has
        // moved on, so a return-to-sheet pointing at it is stale now too.
        setReturnToRequestSheet(null);
      }
    } finally {
      setSaving(false);
    }
  };

  const sendFinanceNotice = async () => {
    if (!financeNotice) return;
    setFinanceSending(true);
    setFinanceError(null);
    try {
      // THE actual approve - decides the Manager stage, notifies the
      // Finance Approver (Teams + email together, plus employee for an
      // advance), all in one call (see requests_api.py's
      // approve_and_notify_finance / approve_advance_and_notify_finance).
      // Only now does the row's state actually change, so only now do we
      // reload the right table.
      const cc = financeCc.length > 0 ? financeCc : undefined;
      // The Approve flow (decision's preview -> this popup's Send) is
      // genuinely concluding right here - clear whichever return-to-sheet
      // this request/advance might have pending, for the same reason the
      // Reject-success/stale branches above do: left set, it would wrongly
      // survive to reopen a stale sheet on some LATER, unrelated cancel.
      if (financeNotice.kind === "request") {
        await approveAndNotifyFinance(
          financeNotice.id,
          financeNotice.comment,
          cc
        );
        setReturnToRequestSheet(null);
        load();
      } else {
        await approveAdvanceAndNotifyFinance(
          financeNotice.id,
          financeNotice.comment,
          cc
        );
        setReturnToAdvanceSheet(null);
        loadAdvances();
      }
      setFinanceNotice(null);
    } catch (err) {
      // A stale decision here (the row was decided elsewhere between
      // opening this popup and clicking Send) closes the popup with a toast
      // and reloads the right table - same handling as every other decision
      // path above. Any other failure keeps the popup open with an inline
      // error so the Manager can retry Send.
      const reload = financeNotice.kind === "request" ? load : loadAdvances;
      if (!handleStaleDecision(err, reload)) {
        setFinanceError(
          err instanceof ApiError
            ? err.message
            : "Could not send this for approval - check your connection and try again."
        );
      } else {
        setFinanceNotice(null);
        if (financeNotice.kind === "request") {
          setReturnToRequestSheet(null);
        } else {
          setReturnToAdvanceSheet(null);
        }
      }
    } finally {
      setFinanceSending(false);
    }
  };

  const submitAdvanceDecision = async () => {
    if (!advanceDecision) return;
    if (advanceDecision.verdict === "Rejected" && !advanceComment.trim()) return;
    setAdvanceSaving(true);
    setAdvanceDecisionError(null);
    try {
      if (advanceDecision.verdict === "Rejected") {
        // Rejecting is immediate and final, exactly as before - no popup.
        await decideAdvanceApproval(
          advanceDecision.approval.advanceId,
          "Rejected",
          advanceComment.trim()
        );
        setAdvanceDecision(null);
        setAdvanceComment("");
        // Same reasoning as submitDecision's identical Reject-success
        // branch above - this flow has genuinely concluded.
        setReturnToAdvanceSheet(null);
        loadAdvances();
        return;
      }
      // Approved: this call only PREVIEWS who Finance would be - nothing
      // is decided yet (see decideAdvanceApproval's own docstring). The
      // advance stays exactly as it is until the "Notify Finance" popup's
      // Send is clicked (sendFinanceNotice), which is the real approve.
      const preview = await decideAdvanceApproval(
        advanceDecision.approval.advanceId,
        "Approved",
        advanceComment || undefined
      );
      setFinanceCc([]);
      setFinanceError(null);
      setFinanceNotice({
        kind: "advance",
        id: advanceDecision.approval.advanceId,
        toName: preview.financeApproverName,
        toEmail: preview.financeApproverEmail,
        comment: advanceComment || undefined
      });
      setAdvanceDecision(null);
      setAdvanceComment("");
      // No loadAdvances() here - nothing has actually changed yet, the
      // row is still Pending until sendFinanceNotice succeeds.
    } catch (err) {
      // Same stale-decision handling as submitDecision's identical catch
      // above - a 409 closes the dialog with a toast and reloads.
      if (!handleStaleDecision(err, loadAdvances)) {
        setAdvanceDecisionError(
          err instanceof ApiError
            ? err.message
            : `Could not ${advanceDecision.verdict === "Approved" ? "approve" : "reject"} this advance - check your connection and try again.`
        );
      } else {
        setAdvanceDecision(null);
        setAdvanceComment("");
        setReturnToAdvanceSheet(null);
      }
    } finally {
      setAdvanceSaving(false);
    }
  };

  const saveRequestReview = async (fields: ReviewRequestFields) => {
    if (!reviewRequestId) return;
    setReviewSaving(true);
    try {
      // Saving only PREVIEWS the Finance Approver - same deferred-commit
      // shape as a plain Approve (see reviewApproval's own docstring). The
      // actual approve-and-route-to-Finance is the SAME
      // approveAndNotifyFinance call a plain Approve uses, fired once the
      // Manager clicks Send on the popup this feeds.
      const preview = await reviewApproval(reviewRequestId, fields);
      setFinanceCc([]);
      setFinanceError(null);
      setFinanceNotice({
        kind: "request",
        id: reviewRequestId,
        toName: preview.financeApproverName,
        toEmail: preview.financeApproverEmail
      });
      setReviewRequestId(null);
    } catch (err) {
      if (!handleStaleDecision(err, load)) {
        toast.error(
          err instanceof ApiError
            ? err.message
            : "Could not save your edits to this request - check your connection and try again."
        );
      }
    } finally {
      setReviewSaving(false);
    }
  };

  const saveAdvanceReview = async (fields: ReviewAdvanceFields) => {
    if (!reviewAdvanceId) return;
    setReviewSaving(true);
    try {
      const preview = await reviewAdvanceApproval(reviewAdvanceId, fields);
      setFinanceCc([]);
      setFinanceError(null);
      setFinanceNotice({
        kind: "advance",
        id: reviewAdvanceId,
        toName: preview.financeApproverName,
        toEmail: preview.financeApproverEmail
      });
      setReviewAdvanceId(null);
    } catch (err) {
      if (!handleStaleDecision(err, loadAdvances)) {
        toast.error(
          err instanceof ApiError
            ? err.message
            : "Could not save your edits to this advance - check your connection and try again."
        );
      }
    } finally {
      setReviewSaving(false);
    }
  };

  const submitResubmit = async () => {
    if (!resubmitTarget || !resubmitComment.trim()) return;
    setResubmitSaving(true);
    setResubmitError(null);
    try {
      // Resubmit has no further chained dialog (unlike Approve's preview ->
      // Finance-notice flow) - this concludes the flow right here, so clear
      // whichever return-to-sheet this id might have pending, same
      // reasoning as every other terminal success/stale branch above.
      if (resubmitTarget.kind === "request") {
        await resubmitApproval(resubmitTarget.id, resubmitComment.trim());
        setReturnToRequestSheet(null);
        load();
      } else {
        await resubmitAdvanceApproval(
          resubmitTarget.id,
          resubmitComment.trim()
        );
        setReturnToAdvanceSheet(null);
        loadAdvances();
      }
      setResubmitTarget(null);
      setResubmitComment("");
    } catch (err) {
      if (
        !handleStaleDecision(
          err,
          resubmitTarget.kind === "request" ? load : loadAdvances
        )
      ) {
        setResubmitError(
          err instanceof ApiError
            ? err.message
            : "Could not send this back to the employee - check your connection and try again."
        );
      }
    } finally {
      setResubmitSaving(false);
    }
  };

  const columns = React.useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor((row) => `${row.requester?.displayName || ""} ${row.requester?.email || ""}`, {
          id: "requester",
          meta: { label: "Requested by" },
          header: "Requested by",
          cell: ({ row }) => {
            const requester = row.original.requester;
            return (
              <div className="min-w-0">
                <p className="truncate font-medium">
                  {requester?.displayName || "-"}
                </p>
                {requester?.designation && (
                  <p className="truncate text-xs text-muted-foreground">
                    {requester.designation}
                  </p>
                )}
              </div>
            );
          }
        }),
        // Travel requests and Other reimbursements share this queue - an
        // Other request has no trip, so its claim type and purpose stand in
        // for the route (same as the Advances tab's own Trip column).
        columnHelper.accessor(
          (row) => (row.requestKind === "other" ? row.flightDetails || "" : tripRoute({ ...row, country: row.country || "" })),
          {
            id: "trip",
            meta: { label: "Trip" },
            header: ({ column }) => (
              <SortableHeader column={column}>Trip</SortableHeader>
            ),
            cell: ({ row }) =>
              row.original.requestKind === "other" ? (
                <div className="min-w-0 text-sm">
                  <p className="truncate font-medium">{row.original.flightDetails || "-"}</p>
                  <p className="text-xs text-muted-foreground">{requestTypeLabel(row.original)}</p>
                </div>
              ) : (
                <TripCell trip={{ ...row.original, country: row.original.country || "" }} />
              )
          }
        ),
        columnHelper.accessor((row) => row.flightDetails || "", {
          id: "purpose",
          meta: { label: "Purpose" },
          header: "Purpose",
          cell: ({ row }) => (
            <span className="block max-w-[13.75rem] truncate text-sm" title={row.original.flightDetails || undefined}>
              {row.original.flightDetails || "-"}
            </span>
          )
        }),
        // This queue's own row (Approval) carries only trip fields +
        // status, not the bill/advance data Amount/Settlement amount need -
        // both cells fetch the full request by id instead (see
        // SettlementAmountCell.tsx's own doc comments on why, and how the
        // two share one fetch rather than duplicating it).
        columnHelper.display({
          id: "amount",
          meta: { label: "Amount" },
          header: "Amount",
          cell: ({ row }) => <AmountCellById requestId={row.original.travelRequestId} />
        }),
        columnHelper.display({
          id: "settlementAmount",
          meta: { label: "Settlement amount" },
          header: "Settlement amount",
          cell: ({ row }) => (
            <SettlementAmountCellById requestId={row.original.travelRequestId} />
          )
        }),
        columnHelper.accessor("status", {
          id: "status",
          meta: { label: "Status" },
          header: ({ column }) => (
            <SortableHeader column={column}>Status</SortableHeader>
          ),
          cell: ({ row }) => {
            const shown = approvalDisplayStatus(row.original);
            return (
              <StatusBadge
                status={shown}
                label={APPROVAL_DISPLAY_STATUS_LABEL[shown]}
              />
            );
          }
        }),
        columnHelper.accessor("createdAt", {
          id: "received",
          meta: { label: "Received" },
          sortFn: "datetime",
          header: ({ column }) => (
            <SortableHeader column={column}>Received</SortableHeader>
          ),
          cell: ({ row }) => (
            <span className="whitespace-nowrap text-sm text-muted-foreground">
              {formatDate(row.original.createdAt)}
            </span>
          )
        }),
        // columnHelper.display({
        //   id: "decided",
        //   meta: { label: "Decided" },
        //   header: "Decided",
        //   cell: ({ row }) => (
        //     <span className="whitespace-nowrap text-sm text-muted-foreground">
        //       {row.original.decidedAt ? formatDate(row.original.decidedAt) : "-"}
        //     </span>
        //   ),
        // }),
        columnHelper.display({
          id: "view",
          enableHiding: false,
          header: "",
          cell: ({ row }) => (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => setViewRequestId(row.original.travelRequestId)}
              >
                <Eye className="mr-1 h-3.5 w-3.5" /> View
              </Button>
              <DownloadReportButton
                travelRequestId={row.original.travelRequestId}
              />
            </div>
          )
        }),
        columnHelper.display({
          id: "actions",
          enableHiding: false,
          header: () => <div className="text-right">Actions</div>,
          cell: ({ row }) => {
            const a = row.original;
            // Only still actionable while it's genuinely Pending the
            // MANAGER's decision - see lib/approval.ts's isActuallyPending
            // for why a.status alone can't tell "Pending" and
            // "ResubmitRequested" apart.
            if (!isActuallyPending(a)) {
              return (
                <div className="text-right text-sm text-muted-foreground">
                  -
                </div>
              );
            }
            return (
              <div className="flex justify-end">
                <SplitActionButton
                  label="Approve"
                  icon={<Check className="mr-1 h-3.5 w-3.5" />}
                  onClick={() => {
                    setComment("");
                    setDecisionError(null);
                    setDecision({ approval: a, verdict: "Approved" });
                  }}
                >
                  <DropdownMenuItem
                    onClick={() => setReviewRequestId(a.travelRequestId)}
                  >
                    <Pencil className="mr-2 h-3.5 w-3.5" /> Review
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      setResubmitComment("");
                      setResubmitError(null);
                      setResubmitTarget({
                        kind: "request",
                        id: a.travelRequestId
                      });
                    }}
                  >
                    <RotateCcw className="mr-2 h-3.5 w-3.5" /> Resubmit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                    onClick={() => {
                      setComment("");
                      setDecisionError(null);
                      setDecision({ approval: a, verdict: "Rejected" });
                    }}
                  >
                    <X className="mr-2 h-3.5 w-3.5" /> Reject
                  </DropdownMenuItem>
                </SplitActionButton>
              </div>
            );
          }
        })
      ]),
    []
  );

  const advanceColumns = React.useMemo(
    () =>
      advanceColumnHelper.columns([
        advanceColumnHelper.accessor(
          (row) => `${row.requester?.displayName || ""} ${row.requester?.email || ""}`,
          {
            id: "requester",
            meta: { label: "Requested by" },
            header: "Requested by",
            cell: ({ row }) => {
              const requester = row.original.requester;
              return (
                <div className="min-w-0">
                  <p className="truncate font-medium">
                    {requester?.displayName || "-"}
                  </p>
                  {requester?.designation && (
                    <p className="truncate text-xs text-muted-foreground">
                      {requester.designation}
                    </p>
                  )}
                </div>
              );
            }
          }
        ),
        // An Other reimbursement advance has no trip - its claim type and
        // purpose stand in for the route (same as ApproverQueue.tsx's own
        // Trip column).
        advanceColumnHelper.accessor(
          (row) => (row.requestKind === "other" ? row.flightDetails || "" : tripRoute({ ...row, country: row.country || "" })),
          {
            id: "trip",
            meta: { label: "Trip" },
            header: "Trip",
            cell: ({ row }) =>
              row.original.requestKind === "other" ? (
                <div className="min-w-0 text-sm">
                  <p className="truncate font-medium">{row.original.flightDetails || "-"}</p>
                  <p className="text-xs text-muted-foreground">{requestTypeLabel(row.original)}</p>
                </div>
              ) : (
                <TripCell trip={{ ...row.original, country: row.original.country || "" }} />
              )
          }
        ),
        advanceColumnHelper.accessor((row) => row.flightDetails || "", {
          id: "purpose",
          meta: { label: "Purpose" },
          header: "Purpose",
          cell: ({ row }) => (
            <span className="block max-w-[13.75rem] truncate text-sm" title={row.original.flightDetails || undefined}>
              {row.original.flightDetails || "-"}
            </span>
          )
        }),
        advanceColumnHelper.accessor((row) => row.amounts[0]?.amount ?? 0, {
          id: "amount",
          meta: { label: "Amount" },
          header: ({ column }) => (
            <SortableHeader column={column}>Amount</SortableHeader>
          ),
          cell: ({ row }) => <AdvanceAmountsCell amounts={row.original.amounts} />
        }),
        advanceColumnHelper.accessor("status", {
          id: "status",
          meta: { label: "Status" },
          header: ({ column }) => (
            <SortableHeader column={column}>Status</SortableHeader>
          ),
          cell: ({ row }) => {
            const shown = approvalDisplayStatus(row.original);
            return (
              <StatusBadge
                status={shown}
                label={APPROVAL_DISPLAY_STATUS_LABEL[shown]}
              />
            );
          }
        }),
        advanceColumnHelper.accessor("createdAt", {
          id: "received",
          meta: { label: "Received" },
          sortFn: "datetime",
          header: ({ column }) => (
            <SortableHeader column={column}>Received</SortableHeader>
          ),
          cell: ({ row }) => (
            <span className="whitespace-nowrap text-sm text-muted-foreground">
              {formatDate(row.original.createdAt)}
            </span>
          )
        }),
        advanceColumnHelper.display({
          id: "view",
          enableHiding: false,
          header: "",
          cell: ({ row }) => (
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => setViewAdvanceId(row.original.advanceId)}
            >
              <Eye className="mr-1 h-3.5 w-3.5" /> View
            </Button>
          )
        }),
        advanceColumnHelper.display({
          id: "actions",
          enableHiding: false,
          header: () => <div className="text-right">Actions</div>,
          cell: ({ row }) => {
            const a = row.original;
            // Same reasoning as the Travel Requests table's identical gate
            // above - isActuallyPending tells "Pending" and "Resubmit"
            // apart, which a.status alone can't.
            if (!isActuallyPending(a)) {
              return (
                <div className="text-right text-sm text-muted-foreground">
                  -
                </div>
              );
            }
            return (
              <div className="flex justify-end">
                <SplitActionButton
                  label="Approve"
                  icon={<Check className="mr-1 h-3.5 w-3.5" />}
                  onClick={() => {
                    setAdvanceComment("");
                    setAdvanceDecisionError(null);
                    setAdvanceDecision({ approval: a, verdict: "Approved" });
                  }}
                >
                  <DropdownMenuItem
                    onClick={() => setReviewAdvanceId(a.advanceId)}
                  >
                    <Pencil className="mr-2 h-3.5 w-3.5" /> Review
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      setResubmitComment("");
                      setResubmitError(null);
                      setResubmitTarget({
                        kind: "advance",
                        id: a.advanceId
                      });
                    }}
                  >
                    <RotateCcw className="mr-2 h-3.5 w-3.5" /> Resubmit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                    onClick={() => {
                      setAdvanceComment("");
                      setAdvanceDecisionError(null);
                      setAdvanceDecision({ approval: a, verdict: "Rejected" });
                    }}
                  >
                    <X className="mr-2 h-3.5 w-3.5" /> Reject
                  </DropdownMenuItem>
                </SplitActionButton>
              </div>
            );
          }
        })
      ]),
    []
  );

  // The row this sheet is currently open for, when it's a travel request
  // (not an advance) AND still genuinely Pending this Manager's own
  // decision - same gate the table's own "actions" column cell uses
  // (isActuallyPending), reused here rather than duplicated as a separate
  // rule, so the sheet's footer and the table row never disagree about
  // whether an action is still available. undefined (not found, e.g. the
  // sheet is showing an advance, or nothing's open) correctly renders no
  // footer actions below.
  const viewRequestApproval = rows.find(
    (r) => r.travelRequestId === viewRequestId
  );
  // Advance-side twin of viewRequestApproval above - same reasoning, same
  // isActuallyPending gate, looked up from advanceRows instead of rows.
  const viewAdvanceApproval = advanceRows.find(
    (r) => r.advanceId === viewAdvanceId
  );

  return (
    <PageContainer fullWidth>
      <PageHeader
        title="My Approvals"
        description="Travel Reimbursement requests and advances routed to you as the reporting manager."
        titleActions={
          <RefreshButton
            onRefresh={view === "requests" ? load : loadAdvances}
            isLoading={view === "requests" ? loading : advancesLoading}
          />
        }
      />

      <Tabs
        value={view}
        onValueChange={(v) => {
          setView(v as View);
          // Switching tabs means moving on from whatever the deep link
          // pointed at - clear its highlight rather than leave a stale
          // highlighted row sitting in a tab the employee navigated away
          // from.
          clearDeepLinkHighlight();
        }}
      >
        <TabsList className="grid w-full grid-cols-2 sm:inline-flex sm:w-auto">
          <TabsTrigger value="requests" className="gap-1.5">
            Travel Requests
            {pendingRequestCount > 0 && (
              <TabCountBadge count={pendingRequestCount} />
            )}
          </TabsTrigger>
          <TabsTrigger value="advances" className="gap-1.5">
            Advances
            {pendingAdvanceCount > 0 && (
              <TabCountBadge count={pendingAdvanceCount} />
            )}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {view === "requests" ? (
        <>
          <DataTable
            columns={columns}
            data={data}
            isLoading={loading}
            filterColumn="requester"
            filterPlaceholder="Search name or email…"
            emptyMessage={EMPTY_COPY[status]}
            persistKey="my-approvals:requests"
            highlightRow={(row) =>
              !!deepLinkedRequestId &&
              row.travelRequestId === deepLinkedRequestId
            }
            toolbarStart={
              <SegmentedTabs
                value={status}
                onChange={(v) => {
                  setStatus(v);
                  clearDeepLinkHighlight();
                }}
                options={[
                  { value: "All", label: "All" },
                  { value: "Pending", label: "Pending" },
                  { value: "ResubmitRequested", label: "Needs changes" },
                  { value: "Approved", label: "Approved" },
                  { value: "Rejected", label: "Rejected" }
                ]}
              />
            }
          />

          <ConfirmDialog
            open={!!decision}
            onOpenChange={(open) =>
              !open &&
              cancelAndMaybeReturnToRequestSheet(() => setDecision(null))
            }
            title={
              decision?.verdict === "Approved"
                ? "Approve this request?"
                : "Reject this request?"
            }
            description={
              decision?.verdict === "Rejected"
                ? "Tell the requester why this is being rejected - required, recorded in the audit log."
                : "Add an optional note for the requester. This decision is recorded in the audit log."
            }
            confirmLabel={
              decision?.verdict === "Approved" ? "Approve" : "Reject"
            }
            destructive={decision?.verdict === "Rejected"}
            pending={saving}
            confirmDisabled={
              decision?.verdict === "Rejected" && !comment.trim()
            }
            onConfirm={submitDecision}
          >
            <Input
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={
                decision?.verdict === "Rejected"
                  ? "Reason for rejection (required)"
                  : "Comment (optional)"
              }
            />
            {decisionError && (
              <p className="text-sm text-destructive">{decisionError}</p>
            )}
          </ConfirmDialog>
        </>
      ) : (
        <>
          <DataTable
            columns={advanceColumns}
            data={advanceData}
            isLoading={advancesLoading}
            filterColumn="requester"
            filterPlaceholder="Search name or email…"
            emptyMessage={ADVANCE_EMPTY_COPY[advanceStatus]}
            persistKey="my-approvals:advances"
            highlightRow={(row) =>
              !!deepLinkedAdvanceId && row.advanceId === deepLinkedAdvanceId
            }
            toolbarStart={
              <SegmentedTabs
                value={advanceStatus}
                onChange={(v) => {
                  setAdvanceStatus(v);
                  clearDeepLinkHighlight();
                }}
                options={[
                  { value: "All", label: "All" },
                  { value: "Pending", label: "Pending" },
                  { value: "Resubmit", label: "Needs changes" },
                  { value: "Approved", label: "Approved" },
                  { value: "Rejected", label: "Rejected" }
                ]}
              />
            }
          />

          <ConfirmDialog
            open={!!advanceDecision}
            onOpenChange={(open) =>
              !open &&
              cancelAndMaybeReturnToAdvanceSheet(() => setAdvanceDecision(null))
            }
            title={
              advanceDecision?.verdict === "Approved"
                ? "Approve this advance?"
                : "Reject this advance?"
            }
            description={
              advanceDecision?.verdict === "Rejected"
                ? "Tell the requester why this is being rejected - required, recorded in the audit log."
                : "Add an optional note for the requester. This decision is recorded in the audit log."
            }
            confirmLabel={
              advanceDecision?.verdict === "Approved" ? "Approve" : "Reject"
            }
            destructive={advanceDecision?.verdict === "Rejected"}
            pending={advanceSaving}
            confirmDisabled={
              advanceDecision?.verdict === "Rejected" && !advanceComment.trim()
            }
            onConfirm={submitAdvanceDecision}
          >
            <Input
              value={advanceComment}
              onChange={(e) => setAdvanceComment(e.target.value)}
              placeholder={
                advanceDecision?.verdict === "Rejected"
                  ? "Reason for rejection (required)"
                  : "Comment (optional)"
              }
            />
            {advanceDecisionError && (
              <p className="text-sm text-destructive">{advanceDecisionError}</p>
            )}
          </ConfirmDialog>
        </>
      )}

      {financeNotice && (
        <FinanceNoticeDialog
          open
          onClose={() => {
            // Only reached by closing WITHOUT sending (a successful Send
            // closes via sendFinanceNotice's own direct setFinanceNotice
            // (null) call, a separate code path) - per this dialog's own
            // design, that leaves the request/advance exactly as it was,
            // still Pending (see financeNotice's own doc comment above), so
            // this is the same "nothing was actually decided" cancel case
            // the other dialogs already handle.
            const closeNotice = () => {
              setFinanceNotice(null);
              setFinanceCc([]);
              setFinanceError(null);
            };
            if (financeNotice.kind === "advance") {
              cancelAndMaybeReturnToAdvanceSheet(closeNotice);
            } else {
              cancelAndMaybeReturnToRequestSheet(closeNotice);
            }
          }}
          toName={financeNotice.toName}
          toEmail={financeNotice.toEmail}
          cc={financeCc}
          onCcChange={setFinanceCc}
          onSend={sendFinanceNotice}
          sending={financeSending}
          error={financeError}
        />
      )}

      <RequestDetailDialog
        travelRequestId={viewRequestId}
        open={!!viewRequestId}
        onOpenChange={(open) => {
          if (!open) {
            closeViewRequestSheet();
          }
        }}
        highlight={highlightOnOpen}
        footerActions={
          viewRequestApproval &&
          isActuallyPending(viewRequestApproval) && (
            // Identical to the table row's own "actions" column cell - same
            // SplitActionButton, same three dropdown items, same state
            // setters - EXCEPT each one also steps aside from this sheet
            // (stepAsideFromRequestSheet) before opening its own dialog.
            // Without that, the sheet stayed open underneath (both are
            // still "open" as far as React is concerned) and its Sheet
            // overlay + the new dialog's own Dialog overlay - both semi-
            // transparent black independently - stacked into what looked
            // like a solid black screen. "Stepping aside" rather than
            // outright closing means canceling the dialog that opens
            // (Review/the Approve-Reject ConfirmDialog/Resubmit) brings
            // this sheet back, rather than dropping the employee onto the
            // bare table - they never asked to leave the request, just to
            // act on it.
            <SplitActionButton
              label="Approve"
              icon={<Check className="mr-1 h-3.5 w-3.5" />}
              onClick={() => {
                stepAsideFromRequestSheet();
                setComment("");
                setDecisionError(null);
                setDecision({ approval: viewRequestApproval, verdict: "Approved" });
              }}
            >
              <DropdownMenuItem
                onClick={() => {
                  stepAsideFromRequestSheet();
                  setReviewRequestId(viewRequestApproval.travelRequestId);
                }}
              >
                <Pencil className="mr-2 h-3.5 w-3.5" /> Review
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  stepAsideFromRequestSheet();
                  setResubmitComment("");
                  setResubmitError(null);
                  setResubmitTarget({
                    kind: "request",
                    id: viewRequestApproval.travelRequestId
                  });
                }}
              >
                <RotateCcw className="mr-2 h-3.5 w-3.5" /> Resubmit
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                onClick={() => {
                  stepAsideFromRequestSheet();
                  setComment("");
                  setDecisionError(null);
                  setDecision({ approval: viewRequestApproval, verdict: "Rejected" });
                }}
              >
                <X className="mr-2 h-3.5 w-3.5" /> Reject
              </DropdownMenuItem>
            </SplitActionButton>
          )
        }
      />
      <AdvanceDetailDialog
        advanceId={viewAdvanceId}
        open={!!viewAdvanceId}
        onOpenChange={(open) => {
          if (!open) {
            closeViewAdvanceSheet();
          }
        }}
        highlight={highlightOnOpen}
        footerActions={
          viewAdvanceApproval &&
          isActuallyPending(viewAdvanceApproval) && (
            // Identical to the Advances table's own "actions" column cell -
            // same SplitActionButton/dropdown shape as the travel-request
            // sheet's footerActions above, pointed at the advance-specific
            // state (advanceDecision/advanceComment/reviewAdvanceId) and
            // resubmitTarget's kind: "advance" instead of "request". Each
            // click steps aside from this sheet first
            // (stepAsideFromAdvanceSheet) - same reasoning as the travel-
            // request sheet above (cancel brings the sheet back; a save
            // that moves forward does not, since stepAsideFromAdvanceSheet
            // only records a RETURN point - whether anything actually
            // returns to it is the next dialog's own onOpenChange's call).
            <SplitActionButton
              label="Approve"
              icon={<Check className="mr-1 h-3.5 w-3.5" />}
              onClick={() => {
                stepAsideFromAdvanceSheet();
                setAdvanceComment("");
                setAdvanceDecisionError(null);
                setAdvanceDecision({
                  approval: viewAdvanceApproval,
                  verdict: "Approved"
                });
              }}
            >
              <DropdownMenuItem
                onClick={() => {
                  stepAsideFromAdvanceSheet();
                  setReviewAdvanceId(viewAdvanceApproval.advanceId);
                }}
              >
                <Pencil className="mr-2 h-3.5 w-3.5" /> Review
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  stepAsideFromAdvanceSheet();
                  setResubmitComment("");
                  setResubmitError(null);
                  setResubmitTarget({
                    kind: "advance",
                    id: viewAdvanceApproval.advanceId
                  });
                }}
              >
                <RotateCcw className="mr-2 h-3.5 w-3.5" /> Resubmit
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                onClick={() => {
                  stepAsideFromAdvanceSheet();
                  setAdvanceComment("");
                  setAdvanceDecisionError(null);
                  setAdvanceDecision({
                    approval: viewAdvanceApproval,
                    verdict: "Rejected"
                  });
                }}
              >
                <X className="mr-2 h-3.5 w-3.5" /> Reject
              </DropdownMenuItem>
            </SplitActionButton>
          )
        }
      />

      <ReviewRequestDialog
        travelRequestId={reviewRequestId}
        open={!!reviewRequestId}
        onOpenChange={(open) =>
          !open &&
          cancelAndMaybeReturnToRequestSheet(() => setReviewRequestId(null))
        }
        onSave={saveRequestReview}
        saving={reviewSaving}
      />
      <ReviewAdvanceDialog
        advanceId={reviewAdvanceId}
        open={!!reviewAdvanceId}
        onOpenChange={(open) =>
          !open &&
          cancelAndMaybeReturnToAdvanceSheet(() => setReviewAdvanceId(null))
        }
        onSave={saveAdvanceReview}
        saving={reviewSaving}
      />

      <ConfirmDialog
        open={!!resubmitTarget}
        onOpenChange={(open) => {
          if (open) return;
          // resubmitTarget serves BOTH sheets (kind: "request" | "advance")
          // from one dialog, unlike Review/decision which each have their
          // own - the kind must be read BEFORE closing clears
          // resubmitTarget, or there'd be no way to tell which (if either)
          // returnTo*Sheet applies.
          const kind = resubmitTarget?.kind;
          if (kind === "advance") {
            cancelAndMaybeReturnToAdvanceSheet(() => setResubmitTarget(null));
          } else {
            cancelAndMaybeReturnToRequestSheet(() => setResubmitTarget(null));
          }
        }}
        title={
          resubmitTarget?.kind === "advance"
            ? "Send this advance back for changes?"
            : "Send this request back for changes?"
        }
        description="The employee will be able to edit it and resubmit - it comes straight back to you, no need to decide it again from scratch."
        confirmLabel="Send back"
        pending={resubmitSaving}
        confirmDisabled={!resubmitComment.trim()}
        onConfirm={submitResubmit}
      >
        <Input
          value={resubmitComment}
          onChange={(e) => setResubmitComment(e.target.value)}
          placeholder="What needs to change? (required)"
        />
        {resubmitError && (
          <p className="text-sm text-destructive">{resubmitError}</p>
        )}
      </ConfirmDialog>
    </PageContainer>
  );
}
